// ---------------------------------------------- modules import
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirebaseReducer,
  isEmpty,
  isLoaded,
  useFirestoreConnect
} from "react-redux-firebase";
import { useHistory } from "react-router-dom";

import { AppState } from "../../../store/rootReducer";
import { IUserData, IOrderedUser } from "../../../store/firebase/common";
import PermissionHelper, {
  MenuIndex
} from "../../../store/helpers/permissionHelper";
import ValidationHelper, {
  pattern
} from "../../../store/helpers/validationHelper";
import { IUser, newUser } from "../../../store/user/common";
import {
  thunkAddUser,
  thunkUpdateUser,
  thunkUpdatePassword,
  thunkDeleteUser
} from "../../../store/user/thunkActions";
import TagHelper from "../../../store/helpers/tagHelper";

// ---------------------------------------------- reduce ordered data
const reduceUsers = (orderedUsers: IOrderedUser[]) =>
  orderedUsers.reduce(
    (currentUsers, orderedUser) => [
      ...currentUsers,
      { ...orderedUser, password: "" }
    ],
    [] as IUser[]
  );

// ---------------------------------------------- custom hooks
export const useAuth = () => {
  // ---------------------------------------------- redux connect
  const auth = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).auth
  );
  const profile = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).profile
  );

  // ---------------------------------------------- local state
  const [areLoaded, setAreLoaded] = useState(
    isLoaded(auth) && isLoaded(profile)
  );
  const [isEmptied, setIsEmptied] = useState(isEmpty(auth) || isEmpty(profile));

  // ---------------------------------------------- effects
  useEffect(() => {
    setAreLoaded(isLoaded(auth) && isLoaded(profile));
    setIsEmptied(isEmpty(auth) || isEmpty(profile));
  }, [auth, profile]);

  return {
    auth,
    profile,
    areLoaded,
    isEmptied
  };
};

export const useUsers = (index: MenuIndex) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const reducerPath = PermissionHelper.getReducer(index);

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: collectionPath,
    where: ["isSuper", "==", false]
  });

  // ---------------------------------------------- redux connect
  const orderedUsers = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedUser[]
  );
  const loading = useSelector((state: AppState) => state[reducerPath].loading);
  const error = useSelector((state: AppState) => state[reducerPath].error);

  // ---------------------------------------------- local state
  const [users, setUsers] = useState<IUser[]>([]);
  const [filtered, setFiltered] = useState<IUser[]>([]);
  const [fetching, setFetching] = useState(true);
  const [criteria, setCriteria] = useState({
    prop: "name",
    keywords: ""
  });

  // ---------------------------------------------- handlers
  const handleFilter = (criterion: string, value: string | null) =>
    setCriteria({
      ...criteria,
      [criterion]: value
    });

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedUsers)) {
      if (!isEmpty(orderedUsers)) {
        const currentUsers = reduceUsers(orderedUsers);

        setUsers(currentUsers);
      } else {
        setUsers([]);
      }

      setFetching(false);
    } else {
      setFetching(true);
    }
  }, [orderedUsers]);

  useEffect(() => {
    const { prop, keywords } = criteria;
    let filtered = users;

    setFiltered(
      filtered.filter(user => RegExp(`${keywords}`, "i").test(user[prop]))
    );
  }, [criteria, users]);

  // ---------------------------------------------- return value
  return {
    loading,
    error,
    filtered,
    fetching,
    criteria,
    onFilter: handleFilter
  };
};

export const useUser = (index: MenuIndex, id?: string) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const reducerPath = PermissionHelper.getReducer(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- router state
  let history = useHistory();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: collectionPath,
    where: ["isSuper", "==", false]
  });

  // ---------------------------------------------- redux connect
  const orderedUsers = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedUser[]
  );
  const loading = useSelector((state: AppState) => state[reducerPath].loading);
  const error = useSelector((state: AppState) => state[reducerPath].error);

  // ---------------------------------------------- local state
  const [user, setUser] = useState<IUser>(newUser());
  const [isResetReady, setIsResetReady] = useState(false);
  const [newPassword, setNewPassword] = useState({
    password: "",
    confirmPassword: ""
  });

  const isUserValid = (confirmPassword?: string) =>
    ValidationHelper.validate(user.name, pattern.user.name) &&
    ValidationHelper.validate(user.permissions, pattern.user.permissions) &&
    (confirmPassword
      ? ValidationHelper.validate(user.email, pattern.login.email) &&
        ValidationHelper.validate(user.password, pattern.login.password) &&
        ValidationHelper.validate(confirmPassword, RegExp(`^${user.password}$`))
      : true);

  const isNewPasswordValid = () =>
    ValidationHelper.validate(newPassword.password, pattern.login.password) &&
    ValidationHelper.validate(
      newPassword.confirmPassword,
      RegExp(`^${newPassword.password}$`)
    );

  // ---------------------------------------------- handlers
  const handleChange = (prop: string, value: string, className: string) =>
    setUser({
      ...user,
      [prop]:
        prop === "tags"
          ? TagHelper.toggleTag(user.tags, parseInt(value))
          : prop === "permissions"
          ? PermissionHelper.togglePermission(
              user.permissions,
              parseInt(value),
              className === "filled-in view" ? 0 : 1
            )
          : prop === "status"
          ? parseInt(value) === 1
            ? true
            : false
          : value
    });

  const handleChangePassword = (prop: string, value: string) =>
    setNewPassword({
      ...newPassword,
      [prop]: value
    });

  const handleToggleReset = () => setIsResetReady(!isResetReady);

  const handleAdd = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (
      auth.uid &&
      PermissionHelper.getModify(profile.permissions, MenuIndex.User)
    ) {
      dispatch(thunkAddUser(user, collectionPath));
      history.push(path);
    }
  };

  const handleUpdate = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (
      auth.uid &&
      PermissionHelper.getModify(profile.permissions, MenuIndex.User)
    ) {
      dispatch(thunkUpdateUser(user, collectionPath));
      history.push(path);
    }
  };

  const handleUpdatePassword = (
    profile: FirebaseReducer.Profile<IUserData>
  ) => {
    if (profile) dispatch(thunkUpdatePassword(user, newPassword.password));

    setIsResetReady(!isResetReady);
  };

  const handleDelete = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (
      auth.uid &&
      PermissionHelper.getModify(profile.permissions, MenuIndex.User)
    ) {
      dispatch(thunkDeleteUser(user.id, collectionPath));
      history.push(path);
    }
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedUsers) && !isEmpty(orderedUsers)) {
      const currentUsers = reduceUsers(orderedUsers);

      const currentUser = currentUsers.find(
        currentUser => currentUser.id === id
      );

      if (currentUser) setUser(currentUser);
    }
  }, [orderedUsers, id]);

  // ---------------------------------------------- return value
  return {
    loading,
    error,
    user,
    isResetReady,
    isUserValid,
    isNewPasswordValid,
    onChange: handleChange,
    onChangePassword: handleChangePassword,
    onToggleReset: handleToggleReset,
    onAdd: handleAdd,
    onUpdatePassword: handleUpdatePassword,
    onUpdate: handleUpdate,
    onDelete: handleDelete
  };
};
